import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Landmark, Lock, KeyRound, Loader2, ShieldCheck, Sparkles,
  Copy, ChevronRight, XCircle, Clock, CheckCircle2, AlertTriangle,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

/**
 * Treasury Activation Flow
 * ═══════════════════════════════════════════════════════════════════
 * Renders the gate CTA + the Handshake dialog. Wraps the entire
 * bank-linking induction into one component so `Wallet.jsx` only has to
 * pass in the current status and an `onActivated` callback.
 *
 * States surfaced (`treasury.status`):
 *   none        → "Request Treasury Authorization"
 *   requested   → "Awaiting Finance Desk clearance…"
 *   key_issued  → "Enter your Activation Key" (Handshake CTA)
 *   activated   → auto-triggers `onActivated` (parent opens bank modal)
 *   rejected    → hard-lock w/ reason + contact prompt
 *   expired     → "Key expired — request a fresh one"
 */
export function TreasuryFlow({ api, treasury: initialTreasury, onRefresh, onActivated, tier }) {
  const [handshakeOpen, setHandshakeOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [treasury, setTreasury] = useState(initialTreasury || { status: "none" });
  const activatedRef = useRef(false);

  // Keep local state in sync with parent's initial hint (only when parent
  // has a MORE-advanced status — never regress a locally observed status).
  useEffect(() => {
    if (!initialTreasury) return;
    setTreasury((cur) => {
      const rank = { none: 0, expired: 1, requested: 2, key_issued: 3,
                      rejected: 3, activated: 4 };
      const a = rank[cur?.status] ?? 0;
      const b = rank[initialTreasury.status] ?? 0;
      return b >= a ? initialTreasury : cur;
    });
  }, [initialTreasury]);

  const refetch = useCallback(async () => {
    try {
      const r = await api.get("/wallet/treasury/status");
      if (r?.data) setTreasury(r.data);
      return r?.data;
    } catch { return null; }
  }, [api]);

  const status = treasury?.status || "none";

  // Auto-fire onActivated exactly once when we transition into activated
  useEffect(() => {
    if (status === "activated" && !activatedRef.current) {
      activatedRef.current = true;
      onActivated?.();
    }
    if (status !== "activated") activatedRef.current = false;
  }, [status, onActivated]);

  const request = useCallback(async () => {
    setBusy(true);
    try {
      await api.post("/wallet/treasury/request");
      toast.success("Treasury Authorization Requested", {
        description: "The Vinci Finance Desk is evaluating your trading credentials.",
      });
      // Fetch fresh status locally FIRST (authoritative), then bubble up
      // to the parent so the rest of the wallet page can react too.
      await refetch();
      await onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit request");
    } finally { setBusy(false); }
  }, [api, refetch, onRefresh]);

  const primary = tier?.primary || "#D8B23E";
  const accent = tier?.accent || "#F6E4A6";
  const glow = tier?.glow || "rgba(216,178,62,0.35)";

  // ─────────── Gate CTA (contextual) ───────────
  let gate = null;
  if (status === "none" || status === "expired") {
    gate = (
      <TreasuryGateCard
        primary={primary} accent={accent} glow={glow}
        title={status === "expired" ? "Activation Key Expired" : "Bank Mapping is Locked"}
        subtitle={
          status === "expired"
            ? "Your prior key lapsed. Request a fresh Treasury Authorization to continue."
            : "The Vinci vault is not open to every partner. Request Treasury Authorization to unlock bank mapping."
        }
        cta="Request Treasury Authorization"
        onClick={request}
        busy={busy}
        testid={status === "expired" ? "treasury-rerequest" : "treasury-request"}
      />
    );
  } else if (status === "requested") {
    gate = (
      <TreasuryPendingCard
        primary={primary} accent={accent} glow={glow}
        title="Finance Desk · Evaluating Credentials"
        subtitle="The Vinci Finance Desk is reviewing your trading history, KYC posture, and network standing. This ritual is deliberately manual — you will receive a Treasury Access Key by email when cleared."
      />
    );
  } else if (status === "key_issued") {
    gate = (
      <TreasuryKeyReadyCard
        primary={primary} accent={accent} glow={glow}
        expiresAt={treasury?.key_expires_at}
        onOpen={() => setHandshakeOpen(true)}
      />
    );
  } else if (status === "rejected") {
    gate = (
      <TreasuryRejectedCard
        reason={treasury?.rejection_reason || "Not disclosed"}
        rejectedAt={treasury?.rejected_at}
      />
    );
  }

  return (
    <>
      {gate}
      <HandshakeDialog
        open={handshakeOpen}
        setOpen={setHandshakeOpen}
        api={api}
        onDone={async () => { await refetch(); await onRefresh?.(); }}
      />
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════
// The Gate — "Request Treasury Authorization"
// ═════════════════════════════════════════════════════════════════════
function TreasuryGateCard({ primary, accent, glow, title, subtitle, cta,
                              onClick, busy, testid }) {
  return (
    <motion.div
      data-testid="treasury-gate"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="relative overflow-hidden border rounded-sm p-6 md:p-8"
      style={{
        borderColor: `${primary}55`,
        background: "linear-gradient(135deg, #0A0906 0%, #14100A 60%, #0A0906 100%)",
        boxShadow: `0 0 60px -20px ${glow} inset`,
      }}
    >
      {/* Ambient orbiting glow */}
      <motion.div
        aria-hidden
        className="absolute -top-24 -right-24 w-56 h-56 rounded-full opacity-40 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${primary}44 0%, transparent 60%)` }}
        animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.55, 0.3] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full grid place-items-center shrink-0"
                style={{ background: `${primary}22`, border: `1px solid ${primary}55` }}>
            <Lock size={18} style={{ color: primary }} />
          </div>
          <div className="max-w-xl">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.32em]"
                  style={{ color: accent }}>
              Vinci Treasury · Privileged Induction
            </div>
            <h3 className="font-serif text-2xl md:text-3xl mt-2 text-[#F5F5F0]">
              {title}
            </h3>
            <p className="text-[13px] text-[#C9C9C2] font-body mt-2 leading-relaxed">
              {subtitle}
            </p>
          </div>
        </div>
        <Button
          data-testid={testid}
          onClick={onClick}
          disabled={busy}
          className="text-black font-grotesk uppercase tracking-[0.28em] text-[11px] rounded-sm px-7 py-6 shrink-0"
          style={{
            background: `linear-gradient(90deg, ${primary}, ${accent}, ${primary})`,
            boxShadow: `0 0 40px ${glow}`,
          }}
        >
          {busy
            ? <><Loader2 size={13} className="animate-spin mr-2" /> Requesting…</>
            : <>{cta}<ChevronRight size={13} className="ml-2" /></>}
        </Button>
      </div>
    </motion.div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Pending — "Evaluating Credentials"
// ═════════════════════════════════════════════════════════════════════
function TreasuryPendingCard({ primary, accent, glow, title, subtitle }) {
  return (
    <motion.div
      data-testid="treasury-pending"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative overflow-hidden border rounded-sm p-6 md:p-8"
      style={{
        borderColor: `${primary}44`,
        background: "linear-gradient(135deg, #0A0906 0%, #12100A 50%, #0A0906 100%)",
        boxShadow: `0 0 50px -25px ${glow} inset`,
      }}
    >
      <div className="flex items-center gap-4">
        <motion.div
          className="w-11 h-11 rounded-full grid place-items-center shrink-0"
          style={{ background: `${primary}22`, border: `1px dashed ${primary}` }}
          animate={{ rotate: 360 }}
          transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
        >
          <ShieldCheck size={18} style={{ color: primary }} />
        </motion.div>
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.32em]"
                style={{ color: accent }}>
            Awaiting Vinci Finance Desk
          </div>
          <h3 className="font-serif text-2xl mt-1.5 text-[#F5F5F0]">{title}</h3>
          <p className="text-[13px] text-[#C9C9C2] font-body mt-2 leading-relaxed max-w-2xl">
            {subtitle}
          </p>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em]"
            style={{ color: accent }}>
        <motion.span
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: primary, boxShadow: `0 0 8px ${primary}` }}
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1.4, repeat: Infinity }}
        />
        Under review · Typical turnaround 24–72 h
      </div>
    </motion.div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Key Ready — "Enter your Activation Key"
// ═════════════════════════════════════════════════════════════════════
function TreasuryKeyReadyCard({ primary, accent, glow, expiresAt, onOpen }) {
  const remaining = useCountdown(expiresAt);
  return (
    <motion.div
      data-testid="treasury-key-ready"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden border rounded-sm p-6 md:p-8"
      style={{
        borderColor: primary,
        background: "linear-gradient(135deg, #0A0906 0%, #1B1401 55%, #0A0906 100%)",
        boxShadow: `0 0 80px -20px ${glow}`,
      }}
    >
      <motion.div
        aria-hidden
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          background: `conic-gradient(from 0deg, transparent 0deg, ${primary}22 90deg, transparent 180deg, ${primary}22 270deg, transparent 360deg)`,
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
      />
      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex items-start gap-4">
          <motion.div
            className="w-12 h-12 rounded-full grid place-items-center shrink-0"
            style={{ background: `${primary}33`, border: `1px solid ${primary}` }}
            animate={{ boxShadow: [`0 0 0px ${glow}`, `0 0 30px ${glow}`, `0 0 0px ${glow}`] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Sparkles size={18} style={{ color: primary }} />
          </motion.div>
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.32em]"
                  style={{ color: accent }}>
              Achievement Unlocked · Treasury Access
            </div>
            <h3 className="font-serif text-2xl md:text-3xl mt-2 text-[#F5F5F0]">
              Your Activation Key has been dispatched.
            </h3>
            <p className="text-[13px] text-[#C9C9C2] font-body mt-2 leading-relaxed max-w-xl">
              Check your inbox for a monumental email from the Vinci Finance Desk.
              Enter the <span style={{ color: primary }}>VNC-TSY-XXXX-XXXX-XXXX</span> code
              in the Handshake below to synchronise your bank with the Vinci vault.
            </p>
            {remaining && (
              <div className="mt-3 inline-flex items-center gap-2 font-mono text-[11px] px-2.5 py-1 rounded-sm"
                    style={{ color: primary, background: `${primary}11`, border: `1px solid ${primary}44` }}>
                <Clock size={11} /> Expires in {remaining}
              </div>
            )}
          </div>
        </div>
        <Button
          data-testid="treasury-open-handshake"
          onClick={onOpen}
          className="text-black font-grotesk uppercase tracking-[0.28em] text-[11px] rounded-sm px-7 py-6 shrink-0"
          style={{
            background: `linear-gradient(90deg, ${primary}, ${accent}, ${primary})`,
            boxShadow: `0 0 40px ${glow}`,
          }}
        >
          <KeyRound size={13} className="mr-2" /> Enter Vault Handshake
        </Button>
      </div>
    </motion.div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Rejected — hard lock
// ═════════════════════════════════════════════════════════════════════
function TreasuryRejectedCard({ reason, rejectedAt }) {
  return (
    <motion.div
      data-testid="treasury-rejected"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="border border-[#E5484D]/50 bg-[#1A0505]/50 rounded-sm p-6 md:p-8"
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-full grid place-items-center shrink-0 bg-[#E5484D]/15 border border-[#E5484D]/40">
          <XCircle size={18} className="text-[#E5484D]" />
        </div>
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#E5484D]">
            Vinci Finance Desk · Access Denied
          </div>
          <h3 className="font-serif text-2xl mt-1.5 text-[#F5F5F0]">
            Treasury Authorization was declined.
          </h3>
          <p className="text-[13px] text-[#C9C9C2] font-body mt-2 leading-relaxed max-w-2xl">
            <b className="text-[#E5484D]">Reason:</b> {reason}
            {rejectedAt ? <span className="text-[#8A8A82] ml-2 italic">· {new Date(rejectedAt).toLocaleString()}</span> : null}
          </p>
          <p className="text-[12px] text-[#8A8A82] font-body mt-3 italic">
            Only an administrator may reset your standing. Contact{" "}
            <a href="mailto:trade@realtymaze.com" className="text-[#D8B23E]">trade@realtymaze.com</a>
            {" "}with your Vinci ID.
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Handshake Dialog — key input + Vault Synchronized animation
// ═════════════════════════════════════════════════════════════════════
function HandshakeDialog({ open, setOpen, api, onDone }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("input");   // input | syncing | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setKey(""); setStage("input"); setErrorMsg(""); setProgress(0);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const submit = async () => {
    if (!key.trim()) return;
    setBusy(true);
    setStage("syncing");
    // Start animation immediately for perceived responsiveness
    const startTime = Date.now();
    const animate = () => {
      const t = (Date.now() - startTime) / 3000;
      setProgress(Math.min(100, Math.round(t * 100)));
      if (t < 1 && stage !== "error") requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
    try {
      await api.post("/wallet/treasury/redeem", { key: key.trim() });
      // Ensure animation has run at least 2.4s for cinematic feel
      const elapsed = Date.now() - startTime;
      if (elapsed < 2400) await new Promise((r) => setTimeout(r, 2400 - elapsed));
      setProgress(100);
      setStage("success");
      // Close the handshake dialog FIRST so Radix can finish its exit
      // animation, then let the parent open the linked-bank modal cleanly.
      setTimeout(async () => {
        setOpen(false);
        // Wait for dialog exit before firing parent refresh (which will
        // in turn schedule setBankOpen). 350ms > Radix exit duration.
        setTimeout(async () => {
          await onDone?.();
        }, 350);
      }, 1200);
    } catch (e) {
      setStage("error");
      setErrorMsg(e.response?.data?.detail || "Invalid activation key");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
      <DialogContent
        data-testid="treasury-handshake-dialog"
        className="bg-[#050505] border border-[#D8B23E]/60 text-[#F5F5F0] max-w-lg overflow-hidden"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
            <KeyRound size={18} className="text-[#D8B23E]" />
            {stage === "success" ? "Vault Synchronized"
              : stage === "syncing" ? "Synchronizing Vault…"
              : "Vault Handshake"}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[#C9C9C2] font-body pt-2">
            {stage === "input"
              ? "Enter the VNC-TSY-XXXX-XXXX-XXXX code from your Treasury Access email."
              : stage === "syncing"
              ? "Aligning your credentials with the Vinci vault. Do not close this window."
              : stage === "success"
              ? "Handshake complete. You may now map your bank account."
              : "The vault rejected the handshake."}
          </DialogDescription>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {stage === "input" && (
            <motion.div key="input"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              <Input
                ref={inputRef}
                data-testid="treasury-key-input"
                value={key}
                onChange={(e) => setKey(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                placeholder="VNC-TSY-XXXX-XXXX-XXXX"
                className="bg-black border-[#D8B23E]/40 text-center font-mono text-lg tracking-[0.3em] py-6"
              />
              {errorMsg && (
                <div data-testid="treasury-handshake-error"
                      className="text-[12px] font-body text-[#E5484D] inline-flex items-center gap-1.5">
                  <AlertTriangle size={12} /> {errorMsg}
                </div>
              )}
            </motion.div>
          )}

          {stage === "syncing" && (
            <motion.div key="syncing"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="py-8"
            >
              <VaultAnimation progress={progress} />
              <div className="mt-8 text-center font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">
                Synchronising · {progress}%
              </div>
            </motion.div>
          )}

          {stage === "success" && (
            <motion.div key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-10 text-center"
            >
              <motion.div
                initial={{ scale: 0, rotate: -90 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 12 }}
                className="mx-auto w-20 h-20 rounded-full grid place-items-center"
                style={{
                  background: "radial-gradient(circle, rgba(216,178,62,0.35) 0%, transparent 70%)",
                }}
              >
                <CheckCircle2 size={44} className="text-[#D8B23E]" />
              </motion.div>
              <div className="mt-4 font-serif text-2xl text-[#F5F5F0]">Access Granted.</div>
              <div className="mt-1 font-body text-[13px] text-[#C9C9C2]">
                Opening the bank mapping form…
              </div>
            </motion.div>
          )}

          {stage === "error" && (
            <motion.div key="error"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="py-6"
              data-testid="treasury-handshake-error"
            >
              <div className="text-center">
                <XCircle size={40} className="text-[#E5484D] mx-auto" />
                <div className="mt-3 font-serif text-2xl text-[#F5F5F0]">Handshake Rejected</div>
                <div data-testid="treasury-handshake-error-detail"
                      className="mt-2 font-body text-[13px] text-[#E5484D]">
                  {errorMsg}
                </div>
                <Button
                  data-testid="treasury-handshake-retry"
                  onClick={() => { setStage("input"); setErrorMsg(""); setKey(""); }}
                  className="mt-6 bg-transparent border border-[#D8B23E] text-[#D8B23E] hover:bg-[#D8B23E] hover:text-black font-grotesk uppercase tracking-[0.28em] text-[10px]"
                >
                  Try Another Key
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {stage === "input" && (
          <DialogFooter>
            <Button variant="outline"
                     onClick={() => setOpen(false)}
                     className="border-white/20 text-[#C9C9C2]">Cancel</Button>
            <Button
              data-testid="treasury-handshake-submit"
              onClick={submit}
              disabled={busy || key.trim().length < 8}
              className="bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk uppercase tracking-[0.28em] text-[11px]"
            >
              {busy
                ? <><Loader2 size={12} className="animate-spin mr-2" /> Syncing…</>
                : <>Initiate Handshake</>}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Vault Synchronizing animation (concentric rings + gold particles) ───
function VaultAnimation({ progress }) {
  const rings = useMemo(() => [90, 70, 50, 32], []);
  return (
    <div className="relative w-full h-56 grid place-items-center" data-testid="treasury-vault-anim">
      <div className="absolute inset-0 grid place-items-center">
        {/* Concentric spinning rings */}
        {rings.map((size, i) => (
          <motion.div
            key={size}
            className="absolute rounded-full border"
            style={{
              width: `${size}%`, height: `${size}%`,
              borderColor: `rgba(216,178,62,${0.15 + i * 0.15})`,
              borderStyle: i % 2 === 0 ? "solid" : "dashed",
              boxShadow: `0 0 ${20 + i * 6}px rgba(216,178,62,${0.05 + i * 0.05}) inset`,
            }}
            animate={{ rotate: i % 2 === 0 ? 360 : -360 }}
            transition={{ duration: 6 + i * 2, repeat: Infinity, ease: "linear" }}
          />
        ))}
        {/* Center core */}
        <motion.div
          className="relative rounded-full grid place-items-center"
          style={{
            width: "22%", height: "22%",
            background: "radial-gradient(circle, rgba(216,178,62,0.45) 0%, rgba(184,115,51,0.15) 60%, transparent 100%)",
            border: "1px solid #D8B23E",
            boxShadow: "0 0 40px rgba(216,178,62,0.6), 0 0 12px rgba(216,178,62,0.9) inset",
          }}
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <Landmark size={20} className="text-[#F6E4A6]" />
        </motion.div>
      </div>
      {/* Progress ring on top */}
      <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100"
            preserveAspectRatio="none">
        <circle cx="50" cy="50" r="46" fill="none"
                 stroke="rgba(216,178,62,0.15)" strokeWidth="0.6" />
        <motion.circle
          cx="50" cy="50" r="46" fill="none"
          stroke="#D8B23E" strokeWidth="0.8" strokeLinecap="round"
          strokeDasharray="289.03"
          animate={{ strokeDashoffset: 289.03 - (289.03 * progress) / 100 }}
          transition={{ duration: 0.3 }}
        />
      </svg>
    </div>
  );
}

// ─── Utility: live countdown "23h 42m" ───
function useCountdown(iso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!iso) return undefined;
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, [iso]);
  if (!iso) return null;
  const target = new Date(iso).getTime();
  const diff = target - now;
  if (diff <= 0) return "expired";
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  return `${h}h ${m}m`;
}
